import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import "../../assets/css/upgrade.css";

const FEATURES = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9 16 11 18 15 14"/>
      </svg>
    ),
    get title() { return tr("Attendance Tracking"); },
    get desc() { return tr("Clock-in / clock-out, break management, and auto-close at end of day."); },
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
    get title() { return tr("Leave Management"); },
    get desc() { return tr("Leave types, balances, approval workflows, and half-day requests."); },
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
      </svg>
    ),
    get title() { return tr("HR Reports"); },
    get desc() { return tr("Attendance summaries, leave reports, overtime analysis, and CSV export."); },
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
    get title() { return tr("Overtime Tracking"); },
    get desc() { return tr("Daily and weekly overtime thresholds with configurable rate multipliers."); },
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/>
      </svg>
    ),
    get title() { return tr("Invoicing"); },
    get desc() { return tr("Create, issue, and track invoices with line items, tax, and client billing profiles."); },
  },
];

export default function UpgradePage() {
  useTranslation();
  const auth = useSelector((state) => state.auth);
  const history = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const launchFreeAccess = Boolean(auth.launch_free_access);
  const launchFreeUntil = auth.launch_free_until;

  async function handleUpgrade() {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post("/api/billing/create-checkout-session", { plan: 'pro', period });
      window.location.href = data.url;
    } catch (err) {
      setError(err.response?.data?.error || "Something went wrong. Please try again.");
      setLoading(false);
    }
  }

  return (
    <div className="upgrade-root">
      <div className="upgrade-inner">
        {/* Back link */}
        <button className="upgrade-back" onClick={() => history(-1)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>{tr("Back")} </button>

        {/* Hero */}
        <div className="upgrade-hero">
          <div className="upgrade-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>{tr("Pro Feature")} </div>
          <h1>{tr("Unlock Pro Features")}</h1>
          <p>
            {launchFreeAccess ? (
              <>{tr("Attendance, leave management, overtime tracking, HR reports, and invoicing are currently unlocked for your workspace through the")} <strong>{tr("Minutezilla launch offer")}</strong>.
              </>
            ) : (
              <>{tr("Attendance, leave management, overtime tracking, HR reports, and invoicing are part of the")} <strong>Minutezilla Pro</strong> {tr("plan. Upgrade to give your team the tools they need.")} </>
            )}
          </p>
        </div>

        {/* Feature grid */}
        <div className="upgrade-grid">
          {FEATURES.map((f) => (
            <div className="upgrade-feature-card" key={f.title}>
              <div className="upgrade-feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="upgrade-cta-box">
          <div className="upgrade-cta-text">
            <h2>{launchFreeAccess ? tr("Your team already has full access") : tr("Ready to unlock Pro for your team?")}</h2>
            <p>
              {launchFreeAccess
                ? `All features are available until ${launchFreeUntil ? new Date(launchFreeUntil).toLocaleDateString() : tr("June 1, 2027")}.`
                : tr("One plan covers your whole workspace. Cancel any time.")}
            </p>
          </div>
          {!launchFreeAccess && <div style={{ display: "flex", gap: 6, background: "#f3f4f6", borderRadius: 8, padding: 3, marginBottom: 14 }}>
            {['monthly', 'yearly'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                style={{
                  flex: 1, padding: "6px 10px", border: "none", borderRadius: 6,
                  fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                  background: period === p ? "#fff" : "transparent",
                  color: period === p ? "#111c14" : "#6b7280",
                  boxShadow: period === p ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                  transition: "all 0.15s",
                }}
              >
                {p === 'yearly' ? tr("Yearly — Save 17%") : tr("Monthly")}
              </button>
            ))}
          </div>}
          {!launchFreeAccess && <div style={{ fontSize: 13, fontWeight: 700, color: "#1a5c30", textAlign: "center", marginBottom: 14 }}>
            {period === 'yearly' ? tr("€5/seat/mo · billed €60/yr") : tr("€6/seat/mo")}
          </div>}
          {error && (
            <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 10 }}>{error}</div>
          )}
          {launchFreeAccess ? (
            <>
              <button
                className="upgrade-cta-btn"
                onClick={() => history("/dashboard")}
              >{tr("Continue to dashboard")} </button>
              <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 10, textAlign: "center" }}>{tr("Billing is paused during the launch free-access period.")} </p>
            </>
          ) : (
            <>
              <button
                className="upgrade-cta-btn"
                onClick={handleUpgrade}
                disabled={loading}
                style={{ opacity: loading ? 0.7 : 1, cursor: loading ? "not-allowed" : "pointer" }}
              >
                {loading ? tr("Redirecting to Stripe…") : tr("Upgrade to Pro")}
              </button>
              <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 10, textAlign: "center" }}>{tr("Secure checkout via Stripe. We never store your card details.")} </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
